import { useQuery } from '@tanstack/react-query';
import type { Permission } from '@voll-intelligence/voll-authz';
import { meRepository } from '@web/data/repositories';
import { queryKeys } from '@web/core/query';
import { useOrgContext } from './use_org_context';

/**
 * Frontend conditional-rendering helper.
 *
 * Backend is still the source of truth — this hook never decides
 * security, only what to render. Mutations always pass through the
 * backend and may still 403.
 *
 * Must be used inside an org-scoped subtree (under `/orgs/$orgSlug/...`)
 * because it relies on `useOrgContext` to know which org to ask
 * permissions for. The org route loader prefetches the same query key,
 * so the first render after navigation has the data resolved.
 */
export function useAuthz() {
  const { orgId } = useOrgContext();

  const query = useQuery({
    queryKey: queryKeys.me.permissions(orgId),
    queryFn: () => meRepository.getMyPermissions(orgId),
    staleTime: 5 * 60 * 1000,
  });

  const permissions = query.data?.permissions ?? [];

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    has: (perm: Permission) => permissions.includes(perm),
    permissions,
  };
}
